import { notifySessionLost } from '@Domain/Api';
import { refreshToken } from '@Infra/FetchLib/fetch';

export type EventStreamFrame = {
	readonly event: string;
	readonly data: string;
};

export type ReadEventStreamOptions = {
	readonly signal: AbortSignal;
	/** Called once per frame. A throw is contained: it never ends the stream. */
	readonly onEvent: (frame: EventStreamFrame) => void;
	/** Called each time a connection is accepted by the server, including after a reconnection. */
	readonly onOpen?: () => void;
	/** Target of an attempt that follows a lost connection; defaults to `url`. */
	readonly reconnectionUrl?: string;
};

const RECONNECTION_DELAYS_MS = [1_000, 2_000, 5_000, 10_000] as const;

const waitFor = (delayMs: number, signal: AbortSignal): Promise<void> =>
	new Promise((resolve) => {
		const finish = (): void => {
			window.clearTimeout(timeoutId);
			signal.removeEventListener('abort', finish);
			resolve();
		};
		const timeoutId = window.setTimeout(finish, delayMs);
		signal.addEventListener('abort', finish, { once: true });
	});

/** Parses one frame (the lines between two blank lines); `null` when it carries no data. */
const parseFrame = (block: string): EventStreamFrame | null => {
	let event = 'message';
	const dataLines: string[] = [];

	for (const line of block.split('\n')) {
		if (line === '' || line.startsWith(':')) {
			continue;
		}

		const separatorIndex = line.indexOf(':');
		const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex);
		const rawValue = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1);
		const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

		if (field === 'event') {
			event = value;
		} else if (field === 'data') {
			dataLines.push(value);
		}
	}

	return dataLines.length > 0 ? { event, data: dataLines.join('\n') } : null;
};

/** Reads the body until it ends; returns whether at least one frame was delivered. */
const consumeBody = async (
	body: ReadableStream<Uint8Array>,
	onEvent: ReadEventStreamOptions['onEvent'],
): Promise<boolean> => {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let hasDeliveredFrame = false;

	try {
		for (;;) {
			const { done, value } = await reader.read();

			if (done) {
				return hasDeliveredFrame;
			}

			buffer += decoder.decode(value, { stream: true }).replace(/\r\n?/g, '\n');

			let boundaryIndex = buffer.indexOf('\n\n');

			while (boundaryIndex !== -1) {
				const frame = parseFrame(buffer.slice(0, boundaryIndex));
				buffer = buffer.slice(boundaryIndex + 2);

				if (frame !== null) {
					hasDeliveredFrame = true;

					try {
						onEvent(frame);
					} catch {
						// A failing consumer must not tear the connection down.
					}
				}

				boundaryIndex = buffer.indexOf('\n\n');
			}
		}
	} finally {
		reader.cancel().catch(() => undefined);
	}
};

/**
 * Reads a Server-Sent Events stream until `signal` is aborted or the server refuses it for good.
 * The returned promise settles only then. It always waits before reconnecting: 1 s after a clean end
 * (body read to its end, at least one frame), where it targets `url`, since the server rotates its
 * streams on a timer and replaying the state on that rotation would overwrite a document being edited.
 * After any other end (network error, refused status, empty body) changes may have been missed: it waits
 * 1 s, 2 s, 5 s, then 10 s (the delay restarts once a frame has been received) and targets
 * `reconnectionUrl ?? url`, and keeps doing so until a connection ends cleanly. A 401 refreshes the
 * token and replays the same attempt once; a second 401 notifies the session loss and stops. A 403 or
 * 404 stops without retry.
 */
const readEventStream = async (url: string, options: ReadEventStreamOptions): Promise<void> => {
	const { signal, onEvent, onOpen, reconnectionUrl } = options;
	let failureCount = 0;
	let hasRefreshedToken = false;
	let hasMissedChanges = false;

	while (!signal.aborted) {
		const attemptUrl = hasMissedChanges ? (reconnectionUrl ?? url) : url;
		let hasDeliveredFrame = false;
		let hasEndedCleanly = false;

		try {
			const response = await fetch(attemptUrl, {
				method: 'GET',
				credentials: 'include',
				headers: { Accept: 'text/event-stream', 'Accept-Language': 'fr' },
				signal,
			});

			if (response.status === 401) {
				if (hasRefreshedToken) {
					notifySessionLost();
					return;
				}

				hasRefreshedToken = true;
				await refreshToken();
				continue;
			}

			if (response.status === 403 || response.status === 404) {
				return;
			}

			if (response.ok && response.body !== null) {
				hasRefreshedToken = false;
				onOpen?.();
				hasDeliveredFrame = await consumeBody(response.body, onEvent);
				hasEndedCleanly = hasDeliveredFrame;
			}
		} catch {
			if (signal.aborted) {
				return;
			}
		}

		if (signal.aborted) {
			return;
		}

		hasMissedChanges = !hasEndedCleanly;
		failureCount = hasEndedCleanly ? 0 : failureCount + 1;
		const delayIndex = Math.min(Math.max(failureCount, 1), RECONNECTION_DELAYS_MS.length) - 1;
		await waitFor(RECONNECTION_DELAYS_MS[delayIndex] ?? 10_000, signal);
	}
};

export default readEventStream;
